import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { CONVERSATIONS } from "@/constants/testIds";
import { toast, Toaster } from "sonner";
import { MessageSquare, X } from "lucide-react";

export default function Conversations() {
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/conversations");
      setRows(data.conversations);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  useEffect(() => {
    load();
  }, []);

  const open = async (id) => {
    setSelected(id);
    setDetail(null);
    try {
      const { data } = await api.get(`/conversations/${id}`);
      setDetail(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div data-testid={CONVERSATIONS.container} className="p-10">
      <Toaster position="top-right" richColors />
      <div className="mb-8">
        <div className="overline mb-3">AI agent output</div>
        <h1 className="font-display text-4xl font-black tracking-tighter">
          Conversations
        </h1>
        <p className="text-neutral-500 mt-2 max-w-xl">
          Every chat your AI agents have had with visitors. Click a row to see
          the full transcript.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <div className="lg:col-span-2 bg-white border border-neutral-200 overflow-hidden">
          {rows.length === 0 ? (
            <div className="p-12 text-center text-neutral-500 text-sm">
              <MessageSquare className="w-8 h-8 mx-auto mb-2 text-neutral-300" />
              No conversations yet.
            </div>
          ) : (
            rows.map((r) => (
              <button
                key={r.id}
                data-testid={CONVERSATIONS.row(r.id)}
                onClick={() => open(r.id)}
                className={`block w-full text-left px-5 py-4 border-b border-neutral-100 hover:bg-neutral-50 transition ${
                  selected === r.id ? "bg-neutral-50 border-l-2 border-l-black" : ""
                }`}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="font-semibold text-sm">{r.lead_name}</span>
                  <span className="text-xs text-neutral-400 font-mono">
                    {r.message_count} msg
                  </span>
                </div>
                <div className="text-xs text-neutral-500 truncate">
                  {r.messages?.[r.messages.length - 1]?.content || "(empty)"}
                </div>
                <div className="text-xs text-neutral-400 mt-1">
                  {new Date(r.updated_at).toLocaleString()}
                </div>
              </button>
            ))
          )}
        </div>

        <div className="lg:col-span-3 bg-white border border-neutral-200 min-h-[500px]">
          {!detail ? (
            <div className="p-12 text-center text-neutral-500 text-sm h-full flex items-center justify-center">
              Select a conversation to view the transcript.
            </div>
          ) : (
            <>
              <div className="px-6 py-4 border-b border-neutral-200 flex items-center justify-between">
                <div>
                  <div className="overline mb-1">Transcript</div>
                  <div className="font-display text-xl font-bold">
                    {detail.lead?.name}
                  </div>
                </div>
                <button
                  onClick={() => {
                    setSelected(null);
                    setDetail(null);
                  }}
                  className="text-neutral-400 hover:text-black"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-6 space-y-3 max-h-[600px] overflow-y-auto">
                {detail.conversation.messages.map((m, i) => (
                  <div
                    key={i}
                    className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                  >
                    <div
                      className={`max-w-[80%] px-3 py-2 text-sm border ${
                        m.role === "user"
                          ? "bg-black text-white border-black"
                          : "bg-white border-neutral-200"
                      }`}
                    >
                      <div className="whitespace-pre-wrap">{m.content}</div>
                      <div
                        className={`text-[10px] mt-1 ${
                          m.role === "user"
                            ? "text-white/60"
                            : "text-neutral-400"
                        }`}
                      >
                        {m.ts ? new Date(m.ts).toLocaleTimeString() : ""}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
